import { sortVoteList } from 'common/Prose/Commands'
import { SORT_ORDER } from 'common/Prose/Commands/constants'
import { schema } from 'prose-core'
import { Node as ProseNode } from 'prosemirror-model'
import { Decoration, EditorView } from 'prosemirror-view'
import React from 'react'
import { contextualActionContext, registerContextualAction } from '../ContextualBlockActions'
import { ReactComponent as SortIcon } from './images/sort16.svg'
import styles from './SortVoteList.module.scss'

export type SortVoteListProps = {
  editorView: EditorView
  nodePos: number
  node: ProseNode
}
export const SortVoteListActionKey = Symbol('sort-vote-list')

export const SortVoteList: React.FC<React.PropsWithChildren<SortVoteListProps>> = ({ editorView, nodePos, node }) => {
  if (node.type !== schema.nodes.vote_list) return null
  if (!editorView.editable) return null
  return (
    <div
      className={styles.SortVoteList}
      onMouseDown={(e) => {
        const {
          state,
          state: { doc },
          dispatch,
        } = editorView
        /**
         * magic number 3, arrived at through guesswork
         * 0: undefined
         * 1: doc
         * 2: vote_list
         * 3: vote_list_item
         */
        sortVoteList(SORT_ORDER.DESCENDING, doc.resolve(nodePos + 3))(state, dispatch)
        e.preventDefault()
        e.stopPropagation()
        editorView.focus()
      }}
    >
      <SortIcon />
    </div>
  )
}

export const registerSortVoteList = () =>
  registerContextualAction(SortVoteListActionKey, {
    active: (ctx) => {
      const vl = findVoteList(ctx)
      if (vl) {
        const [node] = vl
        return node.childCount > 1 // only show if there are more than 1 items
        /** if you want the icon to show up only if the list it unsorted: */
        // const [node] = vl
        // const voteListItems = getVoteListItems(node as StrongNode<typeof nodes.vote_list>)
        // return !isVoteListSorted(toTree(voteListItems), SORT_ORDER.DESCENDING)
      }
      return false
    },
    containerDeco: (caretCtx, hoverCtx) => {
      const findVoteListPosition = (ctx: contextualActionContext) =>
        ctx.reduce<number | null>(
          (foundPos, [node, pos]) => (node.type === schema.nodes.vote_list ? pos + 1 : foundPos),
          null,
        )

      const makeDeco = () => {
        const el = document.createElement('div')
        el.className = styles.SortVoteListContainer
        return el
      }
      const hoverPos = hoverCtx ? findVoteListPosition(hoverCtx) : null
      const caretPos = caretCtx ? findVoteListPosition(caretCtx) : null
      const decos: Decoration[] = []
      if (hoverPos !== null) {
        decos.push(Decoration.widget(hoverPos, makeDeco, { key: 'sort-vote-list-container' }))
      }
      if (caretPos !== null && caretPos !== hoverPos) {
        decos.push(Decoration.widget(caretPos, makeDeco, { key: 'sort-vote-list-container' }))
      }
      return decos
    },
    getPortals: (view, ps) => {
      const caretContext = ps.caretContext
      const hoveredContext = ps.hoveredContext
      let caretVL = caretContext ? findVoteList(caretContext) : null
      let hoveredVL = hoveredContext ? findVoteList(hoveredContext) : null
      const containers = document.getElementsByClassName(styles.SortVoteListContainer)
      const portals = []

      if (caretVL && containers[0]) {
        const [node, pos] = caretVL
        portals.push({
          element: <SortVoteList editorView={view} nodePos={pos} node={node} />,
          container: containers[0],
          key: `sort-vote-list-caret`,
        })
      }
      if (hoveredVL && containers.length) {
        const [node, pos] = hoveredVL
        const container = caretVL ? containers[1] : containers[0]
        if (pos !== caretVL?.[1] && container) {
          portals.push({
            element: <SortVoteList editorView={view} nodePos={pos} node={node} />,
            container,
            key: `sort-vote-list-hovered`,
          })
        }
      }

      return portals
    },
  })

const findVoteList = (ctx: contextualActionContext) =>
  ctx.reduce<[node: ProseNode, pos: number] | null>(
    (foundPos, [node, pos]) => (node.type === schema.nodes.vote_list ? [node, pos + 1] : foundPos),
    null,
  )
